import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { CategorySection, GEM_GRID_SX } from '../GemCard.js';
import { CHECKBOX_SX } from '../Pill.js';
import type { ResultSelection } from '../workflows.types.js';
import { ElementCard } from './brief/ElementCard.js';
import { type ElementPool, poolCategoriesOf, provenElementKeyOf } from './elementPool.js';

interface ProvenElementsViewProps {
  pool: ElementPool;
  selection?: ResultSelection;
}

export function ProvenElementsView({ pool, selection }: ProvenElementsViewProps): ReactNode {
  const total = pool.elements.length;
  const picked = selection?.selectedIds.length ?? 0;
  const elementSelection =
    selection === undefined
      ? undefined
      : {
          selectedKeys: selection.selectedIds,
          onToggle: selection.onToggle,
          onToggleAll: selection.onToggleAll,
        };

  return (
    <Stack sx={{ gap: 4 }} data-testid="proven-elements">
      <Stack
        direction="row"
        sx={{ px: 1, gap: 2, alignItems: 'center', justifyContent: 'space-between' }}
      >
        {selection === undefined ? (
          <Box />
        ) : (
          <Stack
            component="label"
            direction="row"
            sx={{ gap: 1, alignItems: 'center', cursor: 'pointer' }}
          >
            <Checkbox
              size="small"
              checked={picked === total}
              indeterminate={picked > 0 && picked < total}
              onChange={() => selection.onToggleAll()}
              sx={CHECKBOX_SX}
              slotProps={{ input: { 'aria-label': 'Select all' } }}
            />
            <Typography sx={{ fontSize: 13 }}>Select all</Typography>
          </Stack>
        )}
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main' }}>
          {total} {total === 1 ? 'PROVEN ELEMENT' : 'PROVEN ELEMENTS'} FOUND
        </Typography>
      </Stack>
      {poolCategoriesOf(pool.elements).map((category) => (
        <CategorySection
          key={category.name}
          category={category.name}
          meta={`${category.elements.length} element${category.elements.length === 1 ? '' : 's'}`}
        >
          <Box sx={GEM_GRID_SX}>
            {category.elements.map((element) => (
              <ElementCard
                key={provenElementKeyOf(element)}
                element={{ ...element, source: 'scan', context: element.feature }}
                kpi={pool.kpi}
                selectKey={provenElementKeyOf(element)}
                selection={elementSelection}
              />
            ))}
          </Box>
        </CategorySection>
      ))}
    </Stack>
  );
}
